import { useGetGenericImageSettings } from "@/api/user-settings";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { GenericImagePreview } from "@/components/images/GenericImagePreview";
import { UniqueImageFullScreen } from "@/components/modal/UniqueImageFullScreen";
import { BadgeCell } from "@/components/table/CustomCell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  attachmentsToGalleryImages,
  galleryIndexForAttachment,
  getDefaultAttachment,
  PublicAttachmentFormat,
  TypeDefaultImage,
} from "@/lib/image";
import { Attachment } from "@/lib/types";
import { SaveSheetButton } from "@/components/form-items/SaveSheetButton";
import { useAuth } from "@/context/auth/AuthContext";
import { ReactNode, useMemo } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

export type FormHeadingMetadataProps<T extends FieldValues> = {
  /** Libellé traduit de type de fiche (clé `fiche{Type}`, ex. ficheArtwork), en majuscules au-dessus du n° d'enregistrement. */
  sheetLabel?: string;
  reference?: string;
  title?: string;
  subtitle?: string;
  publicationStatus?: string;
  form: UseFormReturn<T>;
  picturesData?: {
    pictures: Attachment[];
    type: TypeDefaultImage;
    size?: PublicAttachmentFormat;
  };
  keyStatus?: string;
  onClick?: () => void;
  /** When true, render the Save (+ Export) actions inside the header card (Essentiel mode). */
  showSaveButton?: boolean;
  /** When true, hide the inline status list (moved into the simplified body). */
  hideStatus?: boolean;
  onSave?: (status?: "published" | "draft" | "bin") => void;
  publishDisabled?: boolean;
  /** Print/Export control shown next to Save in the header card. */
  exportAction?: ReactNode;
  /** « Épingler à l'accueil », à côté d'Exporter (affiché sur toutes les fiches). */
  pinAction?: ReactNode;
  /** When true, hide the "Gérer les photos" button (keep the preview image). */
  hidePhotosButton?: boolean;
  /** Extra badges shown next to the publication status (ex. « Portfolio »). */
  badges?: ReactNode;
  /** Shown between the registration reference and the title (ex. « Fait partie du portfolio »). */
  referenceExtra?: ReactNode;
};

export const FormHeadingMetadata = <T extends FieldValues>({
  sheetLabel,
  reference,
  title,
  subtitle,
  publicationStatus,
  picturesData,
  onClick,
  form,
  keyStatus,
  showSaveButton = false,
  hideStatus = false,
  badges,
  referenceExtra,
  onSave,
  publishDisabled,
  exportAction,
  pinAction,
  hidePhotosButton = false,
}: FormHeadingMetadataProps<T>) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader";
  // La punaise suit Enregistrer / Exporter quand ces boutons sont affichés,
  // sinon elle a sa propre ligne.
  const hasSaveRow = showSaveButton && onSave !== undefined;
  const hasStatusRow = Boolean(keyStatus) && !hideStatus;
  const { data: genericImageSettings } = useGetGenericImageSettings();

  const defaultAttachment = useMemo(() => {
    if (!picturesData) return null;
    return getDefaultAttachment(picturesData.pictures, picturesData.type);
  }, [picturesData]);

  const isGenericImage = defaultAttachment?.isGenericImage === true;

  const galleryImages = useMemo(
    () =>
      picturesData ? attachmentsToGalleryImages(picturesData.pictures) : [],
    [picturesData],
  );

  const galleryInitialIndex = useMemo(() => {
    if (!defaultAttachment || galleryImages.length === 0) {
      return 0;
    }
    return galleryIndexForAttachment(galleryImages, defaultAttachment.id);
  }, [defaultAttachment, galleryImages]);

  const selectedGalleryItem = useMemo(() => {
    if (galleryImages.length === 0) {
      return null;
    }
    return galleryImages[galleryInitialIndex] ?? galleryImages[0];
  }, [galleryImages, galleryInitialIndex]);

  const fallbackPreviewUrl = useMemo(() => {
    if (!defaultAttachment) return "";
    return (
      defaultAttachment[picturesData?.size ?? "mediumPublicUrl"] ??
      defaultAttachment.mediumPublicUrl ??
      defaultAttachment.publicUrl ??
      defaultAttachment.largePublicUrl ??
      defaultAttachment.posterPublicUrl ??
      ""
    );
  }, [defaultAttachment, picturesData?.size]);

  const previewUrl = selectedGalleryItem?.url || fallbackPreviewUrl;
  const previewLargeUrl = selectedGalleryItem?.largeUrl || previewUrl;
  const isMotionPreview =
    selectedGalleryItem?.kind === "video" ||
    selectedGalleryItem?.kind === "embed";

  const pathname = window.location.pathname.split("/").slice(1);
  const type = pathname[1];

  const isAuthorOrCollector = useMemo(() => {
    return type === "author" || type === "collector";
  }, [type]);

  return (
    <div className="max-w-screen-2xl mx-auto p-8 flex justify-between items-start">
      {/* 26px : n° → titre, et artiste → statut (pas de gap-4). */}
      <div className="flex flex-col w-full">
        <div className="flex flex-col w-3/4 h-fit">
          {(sheetLabel || reference) && (
            <div className="flex flex-col gap-1">
              {sheetLabel && (
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[21px] font-bold font-['Instrument Sans'] text-primary uppercase leading-normal">
                    {sheetLabel}
                  </p>
                  {publicationStatus && !isAuthorOrCollector && (
                    <BadgeCell
                      value={t(publicationStatus)}
                      variant={
                        publicationStatus === "published"
                          ? "published"
                          : "draft"
                      }
                    />
                  )}
                  {badges}
                </div>
              )}
              {reference && (
                <p className="text-left text-[16px] font-normal font-['Instrument Sans'] leading-normal text-gray-950">
                  {reference}
                </p>
              )}
              {referenceExtra}
            </div>
          )}
          {(title || subtitle) && (
            <div className={sheetLabel || reference ? "mt-[26px]" : undefined}>
              {title && (
                <p
                  className="text-left text-[28px] font-bold font-['Instrument Sans'] leading-tight text-gray-950"
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              )}
              {subtitle && (
                <p
                  className="text-left text-[16px] font-normal font-['Instrument Sans'] leading-normal text-gray-950"
                  dangerouslySetInnerHTML={{
                    __html: subtitle,
                  }}
                />
              )}
            </div>
          )}
          {showSaveButton && onSave && (
            <div className="mt-4 flex items-center gap-3">
              <SaveSheetButton
                onSave={onSave}
                publishDisabled={publishDisabled}
                className="pt-[9px] pb-[9px]"
              />
              {exportAction}
              {pinAction}
            </div>
          )}
          {/* Fiches sans statut (ex. Multiple) : même rangée Enregistrer + Exporter + Épingler. */}
          {!hasSaveRow && !hasStatusRow && (onSave || exportAction || pinAction) && (
            <div className="mt-4 flex items-center gap-3">
              {!isReader && onSave && (
                <SaveSheetButton
                  onSave={onSave}
                  publishDisabled={publishDisabled}
                  className="pt-[9px] pb-[9px]"
                />
              )}
              {exportAction}
              {pinAction}
            </div>
          )}
        </div>
        {keyStatus && !hideStatus && (
          <div className="mt-[26px]">
            <Form {...form}>
              {/* Vue Pro : Enregistrer + Exporter côte à côte sous le statut. */}
              <div className="flex flex-col gap-3">
                <div className="w-1/4">
                  <ModifiableList
                    name={keyStatus}
                    listKey="liste-status"
                    labelClassName="text-primary text-[16px] font-normal font-['Instrument Sans'] leading-normal"
                    inputClassName="font-bold"
                  />
                </div>
                <div className="flex items-center gap-3">
                  {!isReader && onSave && (
                    <SaveSheetButton
                      onSave={onSave}
                      publishDisabled={publishDisabled}
                      className="pt-[9px] pb-[9px]"
                    />
                  )}
                  {exportAction}
                  {pinAction}
                </div>
              </div>
            </Form>
          </div>
        )}
      </div>
      {picturesData && (
        <div className="flex items-end justify-end w-1/4 relative">
          <div
            className={
              isMotionPreview
                ? "relative flex h-[250px] w-full items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50"
                : "relative w-full max-h-[250px] overflow-hidden"
            }
          >
            {isGenericImage ? (
              <GenericImagePreview
                settings={genericImageSettings}
                className="w-full h-[250px]  border border-solid"
                emptyLabel={t("genericImage.preview.emptyState")}
              />
            ) : (
              (selectedGalleryItem || previewUrl) && (
                <UniqueImageFullScreen
                  media={{
                    url: previewUrl,
                    largeUrl: previewLargeUrl,
                    alt: selectedGalleryItem?.alt || "",
                    className: isMotionPreview
                      ? "h-full w-full"
                      : "w-full max-h-[250px] object-contain object-right",
                    kind: selectedGalleryItem?.kind,
                    videoUrl: selectedGalleryItem?.videoUrl ?? null,
                    embedProvider: selectedGalleryItem?.embedProvider,
                    embedVideoId: selectedGalleryItem?.embedVideoId,
                    embedUrl: selectedGalleryItem?.embedUrl,
                  }}
                  gallery={galleryImages.length > 0 ? galleryImages : undefined}
                  initialIndex={galleryInitialIndex}
                />
              )
            )}
            {!selectedGalleryItem && !previewUrl && !isGenericImage && (
              <div className="w-full h-[100px] bg-white rounded-lg"></div>
            )}
            {!hidePhotosButton && (
              <Button
                variant="white"
                className="absolute bottom-1 right-1 text-gray-950 text-sm flex gap-2 bg-blue-50 hover:bg-blue-100 border border-slate-200"
                onClick={onClick}
              >
                {t("formulaireBannierePhotosBouton")}
                <Badge variant="outlined">
                  {picturesData?.pictures.length ?? "?"}
                </Badge>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
